import React, { useState, useRef, useEffect } from 'react'
import { LogOut } from 'lucide-react'
import { colors } from '@diversioteam/diversio-ds'

import { IProfileMenuProps } from './ProfileMenu.types'
import { createMenuItems } from './ProfileMenu.data'
import * as S from './ProfileMenu.styles'

import { useAppNavigation } from '@/hooks/useAppNavigation'
import { useImpersonationStatus } from '@/hooks/impersonation/useImpersonationStatus'
import { NavigationRoutes } from '@/router/navigation.types'
import { getUserDisplayName } from '@/types/user'

export const ProfileMenu: React.FC<IProfileMenuProps> = ({ user, onLogout, showOnlyLogout = false }) => {
    const [isOpen, setIsOpen] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const { navigateToPath } = useAppNavigation()
    const { status } = useImpersonationStatus()

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false)
            }
        }

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isOpen])

    const getInitials = (name: string) => {
        return name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2)
    }

    const handleMenuItemClick = (screen: 'profile' | 'notification-settings' | 'connect') => {
        // Use app navigation with enums
        if (screen === 'profile') {
            navigateToPath(NavigationRoutes.PROFILE)
        } else if (screen === 'notification-settings') {
            navigateToPath(NavigationRoutes.NOTIFICATION_SETTINGS)
        } else if (screen === 'connect') {
            navigateToPath(NavigationRoutes.CONNECT)
        }
        setIsOpen(false)
    }

    const menuItems = createMenuItems(handleMenuItemClick, user.role)

    return (
        <div ref={dropdownRef} style={{ position: 'relative' }}>
            <S.ProfileButton onClick={() => setIsOpen(!isOpen)} aria-label="Profile menu" aria-expanded={isOpen}>
                <S.AvatarContainer>
                    {user.avatarUrl ? (
                        <S.AvatarImage src={user.avatarUrl} alt={user.fullName} />
                    ) : (
                        <S.AvatarFallback>{getInitials(user.fullName)}</S.AvatarFallback>
                    )}
                </S.AvatarContainer>
            </S.ProfileButton>

            {isOpen && (
                <div
                    style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        marginTop: '0.5rem',
                        width: '16rem',
                        backgroundColor: colors.primaryWhite,
                        borderRadius: '0.5rem',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                        border: `1px solid ${colors.primaryGray1}`,
                        zIndex: 50,
                    }}
                >
                    <S.DropdownHeader>
                        <S.ProfileInfo>
                            {!status?.is_impersonating && (
                                <S.LargeAvatarContainer>
                                    {user.avatarUrl ? (
                                        <S.AvatarImage src={user.avatarUrl} alt={user.fullName} />
                                    ) : (
                                        <S.AvatarFallback>{getInitials(user.fullName)}</S.AvatarFallback>
                                    )}
                                </S.LargeAvatarContainer>
                            )}
                            <S.UserDetails>
                                <S.UserName>
                                    {user.fullName}
                                    {status?.is_impersonating && (
                                        <S.ImpersonationChip>Impersonating</S.ImpersonationChip>
                                    )}
                                </S.UserName>
                                <S.UserRole>{getUserDisplayName(user.role)}</S.UserRole>
                            </S.UserDetails>
                        </S.ProfileInfo>
                    </S.DropdownHeader>

                    <S.MenuItemsList>
                        {/* Only show menu items when not in logout-only mode */}
                        {!showOnlyLogout &&
                            menuItems.map((item, index) => (
                                <S.MenuItem key={index} onClick={item.onClick} $showDivider={false}>
                                    <S.MenuIcon>
                                        <item.icon className="w-full h-full" />
                                    </S.MenuIcon>
                                    <span>{item.label}</span>
                                </S.MenuItem>
                            ))}

                        {/* Always show logout - with divider only if other items are shown */}
                        <S.MenuItem onClick={onLogout} $showDivider={!showOnlyLogout}>
                            <S.MenuIcon>
                                <LogOut className="w-full h-full" />
                            </S.MenuIcon>
                            <span>Logout</span>
                        </S.MenuItem>
                    </S.MenuItemsList>
                </div>
            )}
        </div>
    )
}
